import React from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Link,
  Divider,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import dayjs from 'dayjs';
var utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const ButtonLink = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
  >
    {label}
  </Button>
);

export default function PSALandingPage() {
  return (
    <Grid container>
      <Grid item xs={12} textAlign="center" padding>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          PSA (P.E.O. Scholar Awards)
        </Typography>
      </Grid>
      <Grid item xs={2} />
      <Grid item xs={8} textAlign="center">
        <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
          The Scholar Award is a one-time, competitive, merit-based award is
          intended to recognize and encourage academic excellence and
          achievement by women in doctoral-level programs. In addition to
          recognizing and encouraging excellence in higher education, these
          awards provide partial support for study and research for women who
          will make significant contributions in their varied fields of
          endeavor. All applicants are nominated by a local P.E.O. chapter.
        </Typography>
      </Grid>
      <Grid item xs={2} />

      <Grid item xs={12} padding>
        <Card
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <CardHeader
            title="Local Chapter Recommendation Process"
            subheader="Once you have selected an applicant, follow the steps below to nominate her. The online nomination form is available every year from August 20 to November 20."
          />
          <CardContent>
            <Grid container xs={12}>
              <Grid item xs={6}>
                <Typography
                  variant="body1"
                  padding
                  sx={{ fontWeight: 'bold', backgroundColor: 'lightblue' }}
                >
                  Step
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body1"
                  padding
                  sx={{ fontWeight: 'bold', backgroundColor: 'lightblue' }}
                >
                  Resources
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1" padding>
                  1. Interview your candidate. Use the tips document to prepare.
                </Typography>
              </Grid>
              <Grid item container xs={6} spacing={1}>
                <Grid item>
                  <ButtonLink
                    href="https://www.peointernational.org/wp-content/uploads/2023/11/InterviewingNominating-Tips-for-Potential-Scholar-Award-Candidates-1.pdf"
                    label="Interviewing/Nominating Tips for Potential Candidates"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} padding>
                <Divider />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1" padding>
                  2. After a successful chapter vote, complete the online PSA
                  Nomination Form. You will need to write a 200-300 word letter
                  that shows why your candidate stands out.
                </Typography>
              </Grid>
              <Grid item container xs={6} spacing={1}>
                <Grid item xs={12}>
                  <ButtonLink
                    href="https://www.peointernational.org/wp-content/uploads/2023/11/tipsforwritingpsanominationletterrev2020.pdf"
                    label="Tips for Writing a Nomination Letter"
                  />
                </Grid>
                <Grid item>
                  <ButtonLink
                    href="https://www.peointernational.org/wp-content/uploads/2025/05/Chapter-Form-Instructions-all-projects-08.18.25.pdf"
                    label="Instructions for Online Form"
                  />
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Grid container xs={12} padding>
              <Grid item xs={12} textAlign="center">
                <Link
                  href="https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A306E30564A7645665648426D59714272334A50463566794B506D55552F3132555438504F4C61386C75316856577859322B563757586F3D"
                  target="_blank"
                  rel="noopener"
                >
                  <Button variant="contained" color="primary">
                    PSA Nomination Form
                  </Button>
                </Link>
              </Grid>
              <Grid item xs={12} textAlign="center">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                >
                  Click the button above to be directed to the CommunityForce
                  application system. CommunityForce is a third party vendor we
                  utilize for managing applications. Separate login credentials
                  will be required.
                </Typography>
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} padding>
        <Card
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <CardHeader
            title="Eligibility Requirements"
            sx={{ pb: 0 }}
            subheader="A woman is eligible for consideration for a P.E.O. Scholar Award if she:"
          />
          <CardContent>
            <Grid container xs={12}>
              <Grid item xs={12}>
                <Typography variant="body1" padding>
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  is nominated by a local chapter of the P.E.O. Sisterhood
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" padding>
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  is a citizen or legal permanent resident of the U.S. or Canada
                  at the time of her nomination
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" padding>
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  at a minimum, has a bachelor’s degree
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" padding>
                <Circle sx={{ fontSize: 10, pr: 2 }} />
                is within two years of her graduation date and has at least one
                full academic year of work remaining to complete her
                doctoral-level degree as measured
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" padding>
                <Circle sx={{ fontSize: 10, pr: 2 }} />
                from the time the first award payment is made (August 1). Those
                in a multiple-degree program (e.g., pursuing both an M.D. and a
                Ph.D.) must be within two years of completing the entire program
                from that date.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" padding>
                <Circle sx={{ fontSize: 10, pr: 2 }} />
                is enrolled in her program at an accredited U.S. or Canadian
                college or university. Study abroad is permitted provided the
                student is enrolled in a U.S. or Canadian postsecondary
                institution.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" padding>
                The applications of individuals who satisfy these eligibility
                criteria will be evaluated on a nondiscriminatory basis without
                consideration of race, age, national origin, religious
                affiliation, disability or P.E.O. connection.
              </Typography>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} padding>
        <Card
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <CardHeader title="Application Process" sx={{ pb: 0 }} />
          <CardContent>
            <Grid container xs={12}>
              <Grid item xs={12}>
                <Typography variant="body1" padding>
                  Applicants are nominated by local P.E.O. chapters. The chapter
                  nomination period is between August 20 and November 20 each
                  year. After a chapter submits a nomination, the P.E.O.
                  Executive Office will email the applicant instructions and a
                  link to an online application.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" padding>
                  The nominee is responsible for completing the online
                  application packet and submitting it by the individually
                  designated due date. Each nominee is given 45 days to complete
                  the application. The nominee’s due date will appear on her
                  application form.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" padding>
                  The form requires information about research or an essay about
                  the significance of the applicant’s chosen field, three
                  letters of recommendation, evidence of scholarly activities,
                  awards, academic records, career objectives, etc.
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" padding>
                All nominees will be notified of their award status by May 1.
                Prospective scholars must agree to certain conditions of
                acceptance.
              </Typography>
              <Link
                href="https://www.peointernational.org/wp-content/uploads/2023/11/psapolicies1.2022final.pdf"
                target="_blank"
                rel="noopener"
              >
                <Typography variant="body1" padding>
                  Find full details in the policies and procedures.
                </Typography>
              </Link>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" padding>
                If you are interested in the P.E.O. Scholar Award but do not
                know a P.E.O. member, you can fill out a potential applicant
                form. Completing this form does not guarantee nomination for an
                award.
              </Typography>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <CardHeader
            title="Nomination Dates for P.E.O. Scholar Awards"
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Grid container xs={12}>
              <Grid item xs={4}>
                <Typography
                  variant="body1"
                  padding
                  sx={{ fontWeight: 'bold', backgroundColor: 'lightblue' }}
                >
                  Nomination by Local Chapter in Fall of:
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="body1"
                  padding
                  sx={{ fontWeight: 'bold', backgroundColor: 'lightblue' }}
                >
                  Academic Year Applying for:
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="body1"
                  padding
                  sx={{ fontWeight: 'bold', backgroundColor: 'lightblue' }}
                >
                  Nominee Graduation Date Range:
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2025
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2026-2027
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  4/2027 - 8/2028
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2026
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2027-2028
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  4/2028 - 8/2029
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2027
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2028-2029
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  4/2027 - 8/2028
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2025
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  2026-2027
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" padding>
                  4/2028 - 8/2029
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <CardHeader
            title="Selection Criteria"
            subheader="The Scholar Awards Board of Trustees evaluate applicants on the following:"
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Grid container xs={12}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  nominee's proposal
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  potential to make significant contributions in her field(s) of
                  endeavor or to make a positive impact on society
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  recommendations from three university professors or
                  professional mentors
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  evidence of scholarly activities (publications, presentations,
                  patents, performances, etc.)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  academic awards and honors (honor societies, scholarships,
                  etc.)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  academic record
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Circle sx={{ fontSize: 10, pr: 2 }} />
                  career objectives
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  <b>Note: </b>It is often better to apply further into the
                  doctoral program when evidence of achievement is more
                  complete, giving a more thorough picture of an applicant’s
                  professional work.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
